import { motion, useInView } from "framer-motion";
import { TrendingUp, ShieldCheck, Zap, Package } from "lucide-react";
import { useRef, useEffect, useState } from "react";

const stats = [
  { icon: TrendingUp, value: "10x", label: "Effizienzsteigerung pro Mitarbeiter", target: 10, suffix: "x" },
  { icon: ShieldCheck, value: "100%", label: "DSGVO-konform, in Europa gehostet", target: 100, suffix: "%" },
  { icon: Zap, value: "24/7", label: "Automatisierte Kommunikation", isNumeric: false },
  { icon: Package, value: "< 2'", label: "Durchschnittliche Antwortzeit" },
];

const CountUp = ({ target, suffix, inView }: { target: number; suffix: string; inView: boolean }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1200;
    const step = Math.max(1, Math.floor(target / 30));
    const interval = duration / (target / step);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, interval);
    return () => clearInterval(timer);
  }, [inView, target]);
  return <>{count}{suffix}</>;
};

const benefits = [
  "Repetitive Aufgaben automatisiert",
  "Skalierung ohne zusätzliches Personal",
  "Datenbasierte Optimierung",
  "Bessere Kunden-Experience",
  "Schnellere Reaktionszeiten",
  "Entwickelt aus eigener Praxis",
];

const ImpactSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <section id="vorteile" className="py-16 md:py-20 relative overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-muted/30" />
      <div className="absolute top-0 left-0 right-0 h-16 section-gradient-top" />
      <div className="absolute bottom-0 left-0 right-0 h-16 section-gradient-bottom" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Messbarer Business Impact
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Unsere KI-Lösungen liefern konkrete, messbare Ergebnisse – in Recruiting und E-Commerce
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center p-6 rounded-2xl bg-card border border-border shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center mx-auto mb-3">
                <stat.icon className="w-6 h-6 text-primary/60" />
              </div>
              <p className="text-3xl md:text-4xl font-bold text-foreground mb-1">
                {stat.target !== undefined ? (
                  <CountUp target={stat.target} suffix={stat.suffix || ""} inView={inView} />
                ) : stat.value}
              </p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          {benefits.map((b) => (
            <div key={b} className="flex items-center gap-3 text-foreground/80">
              <div className="w-2 h-2 rounded-full bg-primary/40 shrink-0" />
              <span className="text-sm">{b}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactSection;
